import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

type BannerOrchestratorState = [string[], Dispatch<SetStateAction<string[]>>];

const Context = createContext<BannerOrchestratorState | undefined>(undefined);

interface BannerOrchestratorProps {
  children: ReactNode;
}

export default function BannerOrchestrator({
  children,
}: BannerOrchestratorProps) {
  return (
    <Context.Provider value={useState<string[]>([])}>
      {children}
    </Context.Provider>
  );
}

// Counter for generating unique banner IDs (prevents hydration mismatch)
let bannerId = 0;

export function useBanner() {
  const bannersContext = useContext(Context);

  if (!(bannersContext instanceof Array)) {
    throw Error("Cannot use useBanner() outside of BannerOrchestrator tree");
  }

  const [banners, setBanners] = bannersContext;

  const id = useRef(`banner-${++bannerId}`);

  const bannerIsVisible = banners[0] === id.current;

  const showBanner = useCallback(() => {
    setBanners((prev) =>
      prev.includes(id.current) ? prev : [...prev, id.current]
    );
  }, [setBanners]);

  const hideBanner = useCallback(() => {
    setBanners((prev) => prev.filter((bannerId) => bannerId !== id.current));
  }, [setBanners]);

  return useMemo(
    () => ({
      showBanner,
      hideBanner,
      bannerIsVisible,
    }),
    [showBanner, hideBanner, bannerIsVisible]
  );
}
